import { useMemo, useCallback } from "react";
import { useFetch } from "hooks/api";
import Card from "components/core/basic/Card";
import { Button } from "components/core/basic/Button";
import axios from "axios";
import FileSaver from "file-saver";
import getURLPrefix from "utils/getUrlPrefix";
import { AnalyticsStatsPanel } from "components/custom/analytics/AnalyticsStatsPanel";
import AnalyticsOverviewChart from "components/custom/analytics/AnalyticsOverviewChart";

import { useDates } from "../dates/DatesContext";
import { FaFileExcel } from "react-icons/fa6";
import { Table } from "../components/table/Table";
import { getCoreRowModel, getFilteredRowModel, useReactTable } from "@tanstack/react-table";
import { createColumnBuilder } from "../components/table/columnBuilder";
import { formatPercent } from "../utils/formatNumber";
import { HStack, VStack } from "../../styled-system/jsx";
import { resetTable } from "../components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../components/core/blocks/MetricSelector";
import { SearchInput } from "../components/fields/base/TextField";
import { css } from "../../styled-system/css";
import { RT8Pagination } from "../components/table/Pagination";

const URL_PREFIX = getURLPrefix();

const columnHelper = createColumnBuilder();

const AnalyticsOverview = ({ shelfRedirect, marketplaceRedirect }) => {
  const { start, end } = useDates();

  const onShelfClick = useCallback(
    (row) => {
      shelfRedirect(row.original.id);
      marketplaceRedirect(row.original.marketplace);
    },
    [marketplaceRedirect, shelfRedirect],
  );

  const { data: shelfOverview } = useFetch(["shelf_stats_overview"], "/api/shelf_stats/", {
    stats_by: "shelf",
    end: end.toISOString(),
    start: start.toISOString(),
  });

  const handleExport = async () => {
    const url = URL_PREFIX + "/api/shelf_stats/";

    const params = {
      end: end.toISOString(),
      start: start.toISOString(),
      report: true,
      stats_by: "shelf",
    };

    const response = await axios({
      method: "GET",
      url: url,
      responseType: "blob",
      params: params,
    });

    const blob = new Blob([response.data], {
      type: "vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
    });

    FileSaver.saveAs(blob, "report.xlsx");
  };

  const tableData = useMemo(() => {
    const formattedData = [];

    for (const [key, value] of Object.entries(shelfOverview?.data ?? {})) {
      formattedData.push({
        id: key,
        name: value.page_1?.name || value.above_fold?.name,
        marketplace: value.page_1?.marketplace || value.above_fold?.marketplace,
        page_1_sos: value.page_1?.share_of_shelf || 0,
        page_1_sos_org: value.page_1?.org_share_of_shelf || 0,
        page_1_sov: value.page_1?.share_of_voice || 0,
        above_fold_sos: value.above_fold?.share_of_shelf || 0,
        above_fold_sos_org: value.above_fold?.org_share_of_shelf || 0,
        above_fold_sov: value.above_fold?.share_of_voice || 0,
        average_position: value.page_1?.position_avg || 0,
      });
    }

    return formattedData;
  }, [shelfOverview]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("marketplace", {
        header: "Market Place",
        size: 100,
      }),
      columnHelper.accessor("name", {
        header: "Shelf",
        size: 130,
        cell: ({ getValue, row }) => (
          <Button onPress={() => onShelfClick(row)} variant="link">
            {getValue()}
          </Button>
        ),
      }),
      columnHelper.accessor("page_1_sos", {
        header: "SOS",
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("page_1_sos_org", {
        header: "SOS Organic",
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("page_1_sov", {
        header: "SOV",
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("above_fold_sos", {
        header: "Above the Fold SOS",
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("above_fold_sos_org", {
        header: "Above the Fold SOS Organic",
        size: 130,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("above_fold_sov", {
        header: "Above the Fold SOV",
        width: 180,
        cell: ({ getValue }) => {
          return formatPercent(getValue() / 100, 1);
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor("average_position", {
        header: "Average Position",
        size: 130,
        cell: ({ getValue }) => {
          return Math.round(getValue());
        },
        enableGlobalFilter: false,
      }),
    ],
    [onShelfClick],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: false,
  });

  return (
    <>
      <AnalyticsStatsPanel />
      <Card>
        <AnalyticsOverviewChart />
        <HStack padding={"small"} fontSize={"large"}>
          <VStack flex="1" alignItems={"start"}>
            <HStack width={"100%"}>
              <Button
                variant={"contentOnly"}
                onPress={() => {
                  resetTable(table);
                }}
                fontSize={"xxlarge"}
              >
                <MdRotateLeft />
              </Button>
              <RT8ColumnSelector table={table} />
              <SearchInput
                aria-label={"Search"}
                isExpandable
                className={css({ flex: "0 1 350px" })}
                value={table.getState().globalFilter}
                onChange={table.setGlobalFilter}
              />
            </HStack>
          </VStack>
          <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
        </HStack>
        <Table table={table} />
        <Button onPress={handleExport} className={css({ marginTop: "medium" })}>
          <FaFileExcel /> Download Report
        </Button>
      </Card>
    </>
  );
};

export default AnalyticsOverview;
